import { useEffect, useState } from "react";
import { Plus, AlertTriangle, Pencil, Trash2, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const CATEGORIES = ["Dog Food", "Cat Food", "Treats", "Toys", "Grooming Supplies", "Accessories", "Health"];
const EMPTY = { name: "", category: "Dog Food", price: "", cost: "", stock: "", low_stock_threshold: 5, sku: "", variants: "", batch: "", expiry_date: "", description: "" };

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [adjDialog, setAdjDialog] = useState(null);
  const [adjForm, setAdjForm] = useState({ delta: "", reason: "" });

  const load = () => {
    api.get("/products", { params: { q } }).then((r) => setProducts(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/inventory/adjustments").then((r) => setAdjustments(r.data)).catch(() => {});
    api.get("/inventory/low-stock").then((r) => setLowStock(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [q]);

  const save = async () => {
    const payload = {
      ...form, price: parseFloat(form.price) || 0, cost: parseFloat(form.cost) || 0,
      stock: parseInt(form.stock) || 0, low_stock_threshold: parseInt(form.low_stock_threshold) || 5,
      sku: form.sku || null, variants: form.variants ? form.variants.split(",").map((v) => v.trim()).filter(Boolean) : [],
      batch: form.batch || null, expiry_date: form.expiry_date || null,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/products", payload);
        toast.success(`Product created with SKU ${data.sku}`);
      }
      setDialog(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Product deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const adjust = async () => {
    try {
      await api.post("/inventory/adjustments", { product_id: adjDialog.id, delta: parseInt(adjForm.delta) || 0, reason: adjForm.reason });
      toast.success("Stock adjusted");
      setAdjDialog(null); setAdjForm({ delta: "", reason: "" }); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const expiringSoon = (p) => p.expiry_date && new Date(p.expiry_date) < new Date(Date.now() + 90 * 86400000);

  return (
    <div className="animate-rise space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="inventory-title">Inventory</h1>
          <p className="text-sm text-slate-500">Real-time stock synced with POS and online shop.</p>
        </div>
        <Button className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => { setEditing(null); setForm(EMPTY); setDialog(true); }} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="low-stock-banner">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">{lowStock.length} product(s) at or below reorder level</p>
            <p className="text-amber-700">{lowStock.map((p) => `${p.name} (${p.stock})`).join(" · ")}</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-[#E2F5F5]">
          <TabsTrigger value="products" data-testid="inv-tab-products">Products</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="inv-tab-adjustments">Adjustment Log</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-4 space-y-3">
          <div className="relative max-w-sm">
            <PackageSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9 bg-white" placeholder="Search name or SKU…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inventory-search" />
          </div>
          <div className="bg-white rounded-2xl border border-[#BFE8E9] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead><TableHead>Product</TableHead><TableHead>Category</TableHead>
                  <TableHead className="text-right">Price</TableHead><TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Stock</TableHead><TableHead>Batch / Expiry</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
                    <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{p.name}</p>
                      {p.variants?.length > 0 && <p className="text-xs text-slate-400">{p.variants.join(" · ")}</p>}
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{p.category}</Badge></TableCell>
                    <TableCell className="text-right font-semibold">{peso(p.price)}</TableCell>
                    <TableCell className="text-right text-slate-500">{peso(p.cost)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold ${p.stock === 0 ? "text-red-600" : p.stock <= p.low_stock_threshold ? "text-amber-600" : "text-[#0E7C84]"}`}>{p.stock}</span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {p.batch && <p className="font-mono">{p.batch}</p>}
                      {p.expiry_date && <p className={expiringSoon(p) ? "text-red-600 font-semibold" : "text-slate-500"}>exp {p.expiry_date}</p>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setAdjDialog(p); setAdjForm({ delta: "", reason: "" }); }} data-testid={`adjust-${p.sku}`}>Adjust</Button>
                        <Button variant="ghost" size="sm" className="h-7" onClick={() => { setEditing(p); setForm({ ...p, variants: (p.variants || []).join(", "), batch: p.batch || "", expiry_date: p.expiry_date || "", sku: p.sku }); setDialog(true); }} data-testid={`edit-${p.sku}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 text-red-500" onClick={() => remove(p.id)} data-testid={`delete-${p.sku}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="adjustments" className="mt-4">
          <div className="bg-white rounded-2xl border border-[#BFE8E9] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>When</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Change</TableHead><TableHead>Reason</TableHead><TableHead>By</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {adjustments.map((a) => (
                  <TableRow key={a.id} data-testid={`adj-${a.id.slice(0, 8)}`}>
                    <TableCell className="text-xs text-slate-500">{a.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="text-sm">{a.product_name} <span className="font-mono text-xs text-slate-400">{a.sku}</span></TableCell>
                    <TableCell className={`text-right font-bold ${a.delta < 0 ? "text-red-600" : "text-emerald-600"}`}>{a.delta > 0 ? `+${a.delta}` : a.delta}</TableCell>
                    <TableCell className="text-xs">{a.reason} <Badge variant="outline" className="ml-1 text-[9px]">{a.type}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{a.staff_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>SKU {editing ? "" : "(auto if blank)"}</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} disabled={!!editing} data-testid="product-sku" /></div>
            <div><Label>Selling price (₱)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" /></div>
            <div><Label>Cost (₱)</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="product-cost" /></div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock" /></div>
            <div><Label>Low-stock alert at</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} data-testid="product-threshold" /></div>
            <div className="col-span-2"><Label>Variants (comma-separated)</Label><Input value={form.variants} onChange={(e) => setForm({ ...form, variants: e.target.value })} placeholder="Small, Medium, Large" data-testid="product-variants" /></div>
            <div><Label>Batch no.</Label><Input value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })} data-testid="product-batch" /></div>
            <div><Label>Expiry date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="product-expiry" /></div>
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={save} disabled={!form.name || !form.price} data-testid="product-save-btn">
            {editing ? "Save changes" : "Create product"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjDialog} onOpenChange={(v) => !v && setAdjDialog(null)}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-display">Adjust stock — {adjDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Current stock: <strong>{adjDialog?.stock}</strong></p>
            <div><Label>Change (+ restock / − damage, spoilage)</Label><Input type="number" value={adjForm.delta} onChange={(e) => setAdjForm({ ...adjForm, delta: e.target.value })} placeholder="e.g. 24 or -3" data-testid="adjust-delta" /></div>
            <div><Label>Reason</Label><Input value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="e.g. Supplier delivery / Expired batch" data-testid="adjust-reason" /></div>
            <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" onClick={adjust} disabled={!adjForm.delta || !adjForm.reason} data-testid="adjust-save-btn">Apply adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
